import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useRef, useState } from 'react';

/**
 * Debounced quick-search input used by grids and explorers.
 *
 * **It must only report a value the user actually typed.** The first version
 * armed its debounce on `[local, onChange]`, and every caller passes an inline
 * arrow — a new function identity on every render. So ANY re-render of the page
 * re-armed the timer and committed the unchanged search term 300ms later, and
 * every caller routes that through `setFilter`, which resets the page to 1.
 *
 * The visible symptom was paging: "next" turned to page 2, rendered it, and
 * 300ms later snapped back to page 1 with a wasted request for page 2 already
 * in flight. It looked like the pager was broken. Nothing was wrong with the
 * pager, the list state, or the API — a search box nobody had touched was
 * quietly re-applying itself.
 *
 * Two changes, and both are needed. The ref stops a parent re-render from
 * re-arming the timer; the equality guard stops a commit that says nothing.
 */
export function SearchBox({
  value,
  onChange,
  placeholder = 'Search…',
  width = 260,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  width?: number | string;
}) {
  const [local, setLocal] = useState(value);

  /*
   * Held in a ref rather than a dependency. Callers pass an inline arrow, so
   * depending on it means depending on "did anything at all re-render", which
   * is not a reason to re-run a debounce.
   */
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // A value applied from outside (a cleared filter, a saved view) replaces what
  // is in the box.
  useEffect(() => setLocal(value), [value]);

  useEffect(() => {
    /*
     * Nothing to say. Without this the box re-commits the term it was already
     * showing — harmless-looking, except every caller treats a search change as
     * a reason to return to page 1.
     */
    if (local === value) return;
    const t = setTimeout(() => onChangeRef.current(local), 300);
    return () => clearTimeout(t);
  }, [local, value]);
  return (
    <TextField
      size="small"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      placeholder={placeholder}
      sx={{ width }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );
}
